<template>
  <div class="company-settings__container">
    <CustomCard
      class="company-settings__card"
      header="Company Settings"
      subHeader="">
      <template #body>
        <div class="company-settings__content">
          <div class="company-logo">
            <PhotoViewer
              :showAvatar="false"
              class="logo"
              @onPhotoChange="onPhotoChange"
              @onPhotoRemove="onPhotoRemove"
              :isBusy="isCompanyLogoBusy"
              :imageUrl="companyLogoUrl"
              ref="photo"
            />
            <div class="logo-message">
              <div class="logo-message__header"> Change Logo</div>
              <div class="logo-message__subheader">Lorem ipsum dolor sit amet consectetur adipisicing elit. Quam, obcaecati.</div>
              <b-button variant="outline-primary" class="upload-button" size="sm" @click="onUploadClick">Upload</b-button>
            </div>
          </div>
          <div class="company-info">
            <b-row>
              <b-col md=4>
                <b-form-group
                  label="Company Name"
                  labelClass="required"
                  :invalid-feedback="forms.companySetting.errors.companyName"
                  description="Set your company name."
                >
                  <b-form-input
                    :state="forms.companySetting.states.companyName"
                    v-model="forms.companySetting.fields.companyName"
                    debounce="500" />
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Contact Number"
                  labelClass="required"
                  :invalid-feedback="forms.companySetting.errors.contactNo"
                  description="Set your company contact no."
                >
                  <b-form-input
                    :state="forms.companySetting.states.contactNo"
                    v-model="forms.companySetting.fields.contactNo"
                    debounce="500" />
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Email"
                  labelClass="required"
                  :invalid-feedback="forms.companySetting.errors.email"
                  description="Set your company email."
                >
                  <b-form-input
                    :state="forms.companySetting.states.email"
                    v-model="forms.companySetting.fields.email"
                    debounce="500" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md=12>
                <b-form-group
                  label="Complete Address"
                  labelClass="required"
                  :invalid-feedback="forms.companySetting.errors.completeAddress"
                  description="Set your company address."
                >
                  <b-form-textarea
                    :state="forms.companySetting.states.completeAddress"
                    v-model="forms.companySetting.fields.completeAddress"
                    debounce="500"
                    rows="6" />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>
      </template>
      <template #card-header-filter>
        <SaveButton class="float-right" @click="onSaveCompanySetting()" :isProcessing="forms.companySetting.isProcessing"/>
      </template>
    </CustomCard>

    <CustomCard
      class="company-settings__card mt-4"
      header="System Settings"
      subHeader="">
      <template #body>
        <div class="company-settings__content">
          <b-row>
            <b-col md=4>
              <b-form-group
                label="Billing Cutoff Day"
                labelClass="required"
                description="Set the billing cutoff day for computing previous balances."
                :invalid-feedback="forms.systemSetting.errors.billingCutoffDay"
              >
                <b-form-input
                  :state="forms.systemSetting.states.billingCutoffDay"
                  v-model="forms.systemSetting.fields.billingCutoffDay"
                  debounce="500" />
              </b-form-group>
            </b-col>
            <b-col md=4>
              <b-form-group
                label="Accounts Receivable Account Title"
                labelClass="required"
                :invalid-feedback="forms.systemSetting.errors.accountsReceivableAccountTitleId"
                description="Set the Accounts Receivable account title."
                :state="forms.systemSetting.states.accountsReceivableAccountTitleId"
              >
                <v-select
                  label="name"
                  :options="options.accountTitles.items"
                  v-model="forms.systemSetting.fields.accountsReceivableAccountTitleId"
                  :reduce="option => option.id"
                  debounce="500"
                  :class=" { 'is-invalid' : !!forms.systemSetting.errors.accountsReceivableAccountTitleId  }"
                  appendToBody />
              </b-form-group>
            </b-col>
            <b-col md=4>
              <b-form-group
                label="Operating Expence Class"
                labelClass="required"
                :invalid-feedback="forms.systemSetting.errors.operatingExpenseAccountClassId"
                description="Set the Operating Expense account class."
                :state="forms.systemSetting.states.operatingExpenseAccountClassId"
              >
                <v-select
                  label="name"
                  :options="options.accountClasses.items"
                  v-model="forms.systemSetting.fields.operatingExpenseAccountClassId"
                  :reduce="option => option.id"
                  debounce="500"
                  :class=" { 'is-invalid' : !!forms.systemSetting.errors.operatingExpenseAccountClassId  }"
                  appendToBody />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md=4>
              <b-form-group
                label="Cash Account Title"
                labelClass="required"
                :invalid-feedback="forms.systemSetting.errors.cashAccountTitleId"
                description="Set the Cash account title."
                :state="forms.systemSetting.states.cashAccountTitleId"
              >
                <v-select
                  label="name"
                  :options="options.accountTitles.items"
                  v-model="forms.systemSetting.fields.cashAccountTitleId"
                  :reduce="option => option.id"
                  debounce="500"
                  :class=" { 'is-invalid' : !!forms.systemSetting.errors.cashAccountTitleId  }"
                  appendToBody />
              </b-form-group>
            </b-col>
            <b-col md=4>
              <b-form-group
                label="Service Income Class"
                labelClass="required"
                :invalid-feedback="forms.systemSetting.errors.serviceIncomeAccountClassId"
                description="Set the Service Income account class."
                :state="forms.systemSetting.states.serviceIncomeAccountClassId"
              >
                <v-select
                  label="name"
                  :options="options.accountClasses.items"
                  v-model="forms.systemSetting.fields.serviceIncomeAccountClassId"
                  :reduce="option => option.id"
                  debounce="500"
                  :class=" { 'is-invalid' : !!forms.systemSetting.errors.serviceIncomeAccountClassId  }"
                  appendToBody />
              </b-form-group>
            </b-col>
            <b-col md=4>
              <b-form-group
                label="Other Income Class"
                labelClass="required"
                :invalid-feedback="forms.systemSetting.errors.otherIncomeAccountClassId"
                description="Set the Other Income account class."
                :state="forms.systemSetting.states.otherIncomeAccountClassId"
              >
                <v-select
                  label="name"
                  :options="options.accountClasses.items"
                  v-model="forms.systemSetting.fields.otherIncomeAccountClassId"
                  :reduce="option => option.id"
                  debounce="500"
                  :class=" { 'is-invalid' : !!forms.systemSetting.errors.otherIncomeAccountClassId  }"
                  appendToBody />
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </template>
      <template #card-header-filter>
        <SaveButton class="float-right" @click="onSaveSystemSetting()" :isProcessing="forms.systemSetting.isProcessing"/>
      </template>
    </CustomCard>
      <!-- <div class="action-bar">
        <SaveButton class="mr-5" @click="onSaveCompanySetting()" :isProcessing="forms.companySetting.isProcessing"/>
      </div> -->
  </div>
</template>
<script>
import { copyValue } from '../../../helpers/extractor'
import { AccountClassApi, AccountTitleApi, AccountTypeApi, CompanySettingApi, SystemSettingApi } from '../../../mixins/api'
import PhotoViewer from '../../components/PhotoViewer'
import { SaveButton } from '../../components'
import { showNotification, validate, reset } from '../../../helpers/forms'
import CustomCard from '../../components/CustomCard'


const companyFields = {
  id: null,
  companyName: null,
  completeAddress: null,
  contactNo: null,
  email: null,
  billingCutoffDay: null
}

const systemFields = {
  id: null,
  billingCutoffDay: null,
  accountsReceivableAccountTitleId: null,
  operatingExpenseAccountClassId: null,
  serviceIncomeAccountClassId: null,
  otherIncomeAccountClassId: null,
  cashAccountTitleId: null
}
export default {
  name: 'Company',
  mixins: [ CompanySettingApi, SystemSettingApi, AccountTitleApi, AccountClassApi ],
  components: {
    PhotoViewer,
    SaveButton,
    CustomCard
  },
  data() {
    return {
      forms: {
        companySetting: {
          isProcessing: false,
          fields: { ...companyFields },
          states: { ...companyFields },
          errors: { ...companyFields },
        },
        systemSetting: {
          isProcessing: false,
          fields: { ...systemFields },
          states: { ...systemFields },
          errors: { ...systemFields },
        }
      },
      isCompanyLogoBusy: false,
      companyLogoUrl: null,
      options: {
        accountTitles: {
          items: []
        },
        accountClasses: {
          items: []
        }
      }
    }
  },
  created() {
    const { companySetting, systemSetting } = this.$store.state
    if (companySetting) {
      const { fields } = this.forms.companySetting
      this.companyLogoUrl = companySetting.logo ? `${process.env.VUE_APP_PUBLIC_PHOTO_URL}${companySetting.logo.path}` : ''
      copyValue(companySetting, fields)
    }

    if (systemSetting) {
      const { fields } = this.forms.systemSetting
      copyValue(systemSetting, fields)
    }

    const { accountTitles, accountClasses } = this.options
    const params = { paginate: false}
    this.getAccountTitleList(params).then(({ data }) => {
      accountTitles.items = data
    })

    this.getAccountClassList(params).then(({ data }) => {
      accountClasses.items = data
    })

  },
  watch: {
    '$store.state.companySetting': function(newVal) {
      const { fields } = this.forms.companySetting
      this.companyLogoUrl = newVal.logo ? `${process.env.VUE_APP_PUBLIC_PHOTO_URL}${newVal.logo.path}` : ''
      copyValue(newVal, fields)
    },
    '$store.state.systemSetting': function(newVal) {
      const { fields } = this.forms.systemSetting
      copyValue(newVal, fields)
    },
  },
  methods: {
    onPhotoChange(file) {
      this.isCompanyLogoBusy = true
      const formData = new FormData();
      const { id } = this.forms.companySetting.fields;
      formData.append('photo', file);

      this.saveCompanyLogo(formData, id).then(({ data }) => {
        this.$store.commit('SET_LOGO', data)
        this.companyLogoUrl = `${process.env.VUE_APP_PUBLIC_PHOTO_URL}company-logo/${data.hashName}`
        this.isCompanyLogoBusy = false
      });
    },
    onPhotoRemove() {
      const { id } = this.forms.companySetting.fields;

      this.deleteCompanyLogo(id).then(({ data }) => {
        this.companyLogoUrl = '';
      });
    },
    onSaveCompanySetting() {
      const { companySetting, companySetting: { fields } } = this.forms
      companySetting.isProcessing = true
      reset(companySetting)
      this.updateCompanySetting(fields, fields.id)
      .then(({ data }) => {
        showNotification(this, 'success', 'Company Setting updated successfully.')
        this.$store.commit('SET_COMPANY_SETTING', data)
        companySetting.isProcessing = false
      }).catch(error => {
        const errors = error.response.data.errors
        companySetting.isProcessing = false
        validate(companySetting, errors)
      })
    },
    onSaveSystemSetting() {
      const { systemSetting, systemSetting: { fields } } = this.forms
      systemSetting.isProcessing = true
      reset(systemSetting)
      this.updateSystemSetting(fields, fields.id)
      .then(({ data }) => {
        showNotification(this, 'success', 'System Setting updated successfully.')
        this.$store.commit('SET_SYSTEM_SETTING', data)
        systemSetting.isProcessing = false
      }).catch(error => {
        const errors = error.response.data.errors
        systemSetting.isProcessing = false
        validate(systemSetting, errors)
      })
    },
    onUploadClick(e) {
      this.$refs.photo.onBrowseClick(e)
    }
  }
}
</script>
<style lang="scss" scoped>
  @import "../../../assets/scss/_shared.scss";

  .company-container {
    display: flex;
    // justify-content: center;
    // align-items: center;
    width: 100%;
    height: 100%;
    padding: 80px;
    flex-direction: column;
    // margin: 15px;
    // margin: 0 auto;
    border: solid 1px;
  }

  .company-logo {
    width: 100%;
    margin: 5px;
    height: 120px;
    // display: flex;
    // align-items: center;
    // justify-content: center;
    display: flex;

    .logo {
      width: 120px;
      height: 120px;
    }
  }

  .company-info {
    width: 100%;
    margin-top: 30px;
  }

  .action-bar {
    background: white;
    width: 100%;
    height: 60px;
    position: sticky;
    left: 0;
    bottom: 0;
    right: 0;
    padding: 5px 0;
    text-align: right;
    z-index: 500
  }


  .company-settings__container {
    padding: 60px;

    @include for-size(phone-only, 200px) {
      padding: 10px;
    }
  }

  .company-settings__card {
    border: solid 1px lightgray;
  }

  .logo-message {
    max-width: 250px;
    height: 100%;
    margin-left: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .logo-message__header {
      font-size: 14pt;
      font-weight: bold;
    }

    .logo-message__subheader {
      font-size: 9pt;
    }

    .upload-button {
      width: 80px;
      height: 40px!important;
      margin-top: 10px;
    }
  }

  .company-settings__content {
    padding: 20px 60px;

    @include for-size(phone-only, 400px) {
      padding: 20px 20px;
    }
  }
</style>
